// ComposedExperienceNeonOracle — Tier-A "neon-oracle" skin for composed experiences.
//
// A per-world treatment of the composed runtime (alongside Obsidian + Aurora),
// proving the dispatcher pattern generalises. Same data + capture contract as
// ComposedExperienceSlide (both consume useComposedExperience), rendered in the
// NEON-ORACLE visual language shared with NeonOracleScenarioSim: a dark terminal,
// neon-glow borders, monospace readouts, scanline vibes, and cyan/lime primaries
// with magenta for the delta/incorrect accent. The standout — a re-rate
// rate-slider that renders a glowing "DELTA READOUT / oscilloscope diff": a ghost
// marker for the earlier reading, a glowing trace between prior and current, and a
// terminal-style `FIRST n ▸ REVISED n · Δ …` line — so a change of mind is made
// visible (§8.12b: juice = cognition). Neon-oracle's signature is the diff trace,
// where Obsidian's was the red string and Aurora's the ribbon/bloom.

import { useEffect, useMemo, useState } from 'react';
import type { Theme } from '../../slideTypes';
import type { ComposedExperienceSlideData } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import type {
  ChoiceBlock,
  CommitBoxBlock,
  CompositionBlock,
  ConfidenceBlock,
  FlagInTextBlock,
  PerspectiveLensBlock,
  RateSliderBlock,
  RevealBlock,
  SelectEvidenceBlock,
  SortZonesBlock,
  StimulusBlock,
} from '../../../../../lib/scenario-builder/composition/types';
import type { Answers, BlockAnswer } from '../../../../../lib/scenario-builder/composition/capture';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import { useThemeWorldAudio } from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';
import { Typewriter } from '../../../../../lib/scenario-themes/Typewriter';
import { useComposedExperience } from './useComposedExperience';

interface Props {
  data: ComposedExperienceSlideData;
  theme: Theme;
  isEditing?: boolean;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

// The generic cue runner bound to the neon-oracle engine.
type Play = ReturnType<typeof useThemeWorldAudio>['play'];

const STYLE_ID = 'neon-oracle-composed-scoped-styles';

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — namespaced under `.norcx` (distinct from NeonOracleScenarioSim's
// `.noracle`). Terminal panels, neon-glow gauge, DELTA READOUT diff, DECRYPTING
// reveal, SIGNAL LOCKED close. Palette threaded in as args (derived from theme).
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(lime: string, cyan: string, magenta: string, bg: string, text: string): string {
  return `
.norcx { position: relative; }
.norcx * { box-sizing: border-box; }

/* terminal panel — dark surface, neon-glow edge */
.norcx .nor-panel {
  position: relative; background: linear-gradient(180deg, ${cyan}0d, transparent), rgba(0,0,0,0.28);
  border: 1px solid ${cyan}33; border-radius: 4px;
  box-shadow: 0 0 0 1px ${cyan}0d, 0 18px 50px rgba(0,0,0,0.5), inset 0 0 60px ${cyan}0a;
  padding: 22px 24px; overflow: hidden;
}
/* thin scanline overlay */
.norcx .nor-panel::before {
  content: ""; position: absolute; inset: 0; pointer-events: none; z-index: 0; opacity: 0.5;
  background: repeating-linear-gradient(0deg, ${cyan}06 0 1px, transparent 1px 3px);
}
.norcx .nor-panel > * { position: relative; z-index: 1; }
.norcx .nor-kicker { font-size: 0.68rem; letter-spacing: 0.26em; text-transform: uppercase; color: ${cyan}; margin-bottom: 8px; font-weight: 600; text-shadow: 0 0 8px ${cyan}80; }
.norcx .nor-prompt { font-family: inherit; font-size: 1.05rem; color: ${text}; margin: 0 0 6px; line-height: 1.4; }
.norcx .nor-body { color: ${text}; font-size: 1rem; line-height: 1.6; margin: 0; }
.norcx .nor-quote { border-left: 2px solid ${magenta}; padding: 6px 0 6px 16px; color: ${text}; }

.norcx textarea.nor-input {
  width: 100%; font-family: inherit; font-size: 1rem; color: ${text}; line-height: 1.55;
  background: rgba(0,0,0,0.4); border: 1px solid ${cyan}33; border-radius: 4px; padding: 12px 14px; resize: vertical;
  transition: border-color .3s, box-shadow .3s;
}
.norcx textarea.nor-input:focus { outline: none; border-color: ${lime}; box-shadow: 0 0 0 1px ${lime}66, 0 0 22px ${lime}33; }

/* gauge readout */
.norcx .nor-scale-val { font-size: 2.6rem; line-height: 1; color: ${lime}; text-shadow: 0 0 24px ${lime}80; text-align: center; letter-spacing: 0.04em; }
.norcx input[type="range"].nor-range { -webkit-appearance: none; appearance: none; width: 100%; height: 6px; border-radius: 999px; outline: none; cursor: grab; border: 1px solid ${cyan}33; background: rgba(0,0,0,0.4); }
.norcx input[type="range"].nor-range:disabled { cursor: default; }
.norcx input[type="range"].nor-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 20px; height: 20px; border-radius: 50%; background: radial-gradient(circle at 35% 30%, #fff, ${lime} 55%, ${cyan}); border: 2px solid ${lime}; box-shadow: 0 0 0 4px ${lime}22, 0 0 20px ${lime}99; }
.norcx input[type="range"].nor-range::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; border: 2px solid ${lime}; background: radial-gradient(circle at 35% 30%, #fff, ${lime} 55%, ${cyan}); box-shadow: 0 0 0 4px ${lime}22, 0 0 20px ${lime}99; }

/* the change-of-mind DELTA READOUT / oscilloscope diff (re-rate standout) */
.norcx .nor-track { position: relative; height: 44px; margin: 16px 0 6px; }
.norcx .nor-rail { position: absolute; top: 50%; left: 0; right: 0; height: 6px; transform: translateY(-50%); border-radius: 999px; background: rgba(0,0,0,0.4); border: 1px solid ${cyan}22; }
.norcx .nor-range.nor-overlay { position: absolute; inset: 0; height: 100%; background: transparent; border: none; z-index: 4; }
.norcx .nor-ghost { position: absolute; top: 50%; width: 14px; height: 14px; border-radius: 50%; transform: translate(-50%,-50%); border: 2px dashed ${magenta}; background: rgba(0,0,0,0.5); box-shadow: 0 0 10px ${magenta}66; z-index: 3; }
.norcx .nor-trace {
  position: absolute; top: 50%; height: 6px; transform: translateY(-50%); border-radius: 999px; z-index: 2;
  background: linear-gradient(90deg, ${magenta}, ${cyan}, ${lime});
  box-shadow: 0 0 16px ${cyan}80, 0 0 26px ${magenta}40;
  transition: left .5s cubic-bezier(0.22,0.61,0.27,1), width .5s cubic-bezier(0.22,0.61,0.27,1);
}
.norcx .nor-trace.pulse::after { content: ""; position: absolute; inset: -6px; border-radius: 999px; background: radial-gradient(circle, ${lime}44, transparent 70%); animation: norPulse 2.2s ease forwards; }
@keyframes norPulse { 0% { opacity: 0; } 30% { opacity: 1; } 100% { opacity: 0.3; } }
.norcx .nor-delta { font-family: inherit; font-size: 0.82rem; letter-spacing: 0.08em; margin-top: 10px; font-weight: 600; }
.norcx .nor-delta .nor-first { color: ${cyan}; text-shadow: 0 0 10px ${cyan}66; }
.norcx .nor-delta .nor-rev { color: ${lime}; text-shadow: 0 0 10px ${lime}66; }
.norcx .nor-delta .nor-d { color: ${magenta}; text-shadow: 0 0 12px ${magenta}80; }
.norcx .nor-ends { display: flex; justify-content: space-between; font-size: 0.72rem; color: ${cyan}aa; margin-top: 2px; letter-spacing: 0.04em; }

.norcx .nor-btn {
  margin-top: 14px; font-family: inherit; font-size: 0.76rem; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: ${bg}; cursor: pointer;
  padding: 11px 22px; border-radius: 4px; border: 1px solid ${lime}; background: linear-gradient(110deg, ${lime}, ${cyan});
  box-shadow: 0 0 22px ${lime}55; transition: transform .3s cubic-bezier(0.22,0.61,0.27,1), box-shadow .3s, opacity .3s;
}
.norcx .nor-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 0 34px ${lime}88; }
.norcx .nor-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* options */
.norcx .nor-opt {
  width: 100%; text-align: left; color: ${text}; background: rgba(0,0,0,0.4); border: 1px solid ${cyan}33;
  border-radius: 4px; padding: 12px 14px; font: inherit; cursor: pointer; transition: border-color .2s, box-shadow .2s, background .2s;
}
.norcx .nor-opt:hover:not(:disabled) { border-color: ${lime}; box-shadow: 0 0 18px ${lime}22; }
.norcx .nor-opt.nor-on { border-color: ${lime}; background: ${lime}12; box-shadow: 0 0 0 1px ${lime}66, 0 0 24px ${lime}33; }
.norcx .nor-opt:disabled { cursor: default; }

/* reveal — DECRYPTING scanline unveil once its ref commits */
.norcx .nor-sealed { border: 1px dashed ${magenta}55; border-radius: 4px; padding: 20px; text-align: center; color: ${magenta}; font-size: 0.76rem; letter-spacing: 0.18em; text-transform: uppercase; background: repeating-linear-gradient(45deg, rgba(0,0,0,0.35) 0 10px, rgba(0,0,0,0.2) 10px 20px); }
.norcx .nor-reveal { position: relative; }
.norcx .nor-reveal .nor-decrypt { animation: norDecrypt 1.4s ease forwards; }
@keyframes norDecrypt {
  0% { clip-path: inset(0 0 100% 0); filter: brightness(1.6) blur(2px); opacity: 0.85; }
  60% { filter: brightness(1.15) blur(0.4px); opacity: 1; }
  100% { clip-path: inset(0 0 0% 0); filter: none; opacity: 1; }
}
.norcx .nor-reveal .nor-sweep {
  content: ""; position: absolute; left: -6%; right: -6%; top: 0; height: 2px;
  background: linear-gradient(90deg, transparent, ${cyan}, #eafdff, ${cyan}, transparent);
  box-shadow: 0 0 22px 6px ${cyan}80; animation: norSweep 1.4s ease forwards; z-index: 5; pointer-events: none;
}
@keyframes norSweep { 0% { top: 0; opacity: 1; } 95% { opacity: 1; } 100% { top: 100%; opacity: 0; } }

/* ending */
.norcx .nor-locked { text-align: center; margin-bottom: 14px; }
.norcx .nor-locked span { font-size: 0.72rem; letter-spacing: 0.28em; text-transform: uppercase; color: ${lime}; text-shadow: 0 0 18px ${lime}80; }
.norcx .nor-locked .nor-line { height: 2px; width: 130px; margin: 12px auto 0; border-radius: 999px; background: linear-gradient(90deg, transparent, ${lime}, ${magenta}, transparent); box-shadow: 0 0 16px ${lime}80; }

@media (prefers-reduced-motion: reduce) {
  .norcx .nor-trace { transition: none !important; }
  .norcx .nor-trace.pulse::after { animation: none !important; opacity: 0.3; }
  .norcx .nor-reveal .nor-decrypt { animation: none !important; clip-path: none !important; filter: none !important; opacity: 1; }
  .norcx .nor-reveal .nor-sweep { display: none; }
}
`;
}

function useScopedStyles(css: string): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = css;
      document.head.appendChild(tag);
    }
    tag.dataset.refcount = String(Number(tag.dataset.refcount || '0') + 1);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────

export function ComposedExperienceNeonOracle({
  data,
  theme,
  isEditing,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  // Palette derived from the theme (like NeonOracleScenarioSim), not hardcoded.
  const lime = theme.colors.primary;
  const cyan = theme.colors.accent || theme.colors.primary;
  const magenta = theme.colors.feedback.incorrectBorder || theme.colors.accent;
  const bg = theme.colors.background;
  const text = theme.colors.text;
  const muted = theme.colors.textMuted;
  const codeFont = theme.fonts.code;

  useScopedStyles(useMemo(() => scopedCss(lime, cyan, magenta, bg, text), [lime, cyan, magenta, bg, text]));

  const { blocks, answers, setAnswer, complete, values, rollup } = useComposedExperience({
    data,
    isEditing,
    onActivityResponse,
    onComplete,
    savedResponse,
  });

  const { enabled: audioOn, toggle: toggleAudio, play } = useThemeWorldAudio('neon-oracle');

  return (
    <div
      className="norcx h-full w-full overflow-y-auto"
      style={{
        background: `radial-gradient(1200px 700px at 50% -5%, ${lime}14, transparent 60%), radial-gradient(900px 600px at 100% 20%, ${cyan}16, transparent 55%), ${bg}`,
        color: text,
        fontFamily: theme.fonts.body,
      }}
    >
      <ThemeWorldAtmosphere worldId="neon-oracle" primary={lime} accent={cyan} background={bg} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 10 }}>
        <div className="mx-auto" style={{ maxWidth: 720, padding: '56px 24px 110px' }}>
          <header style={{ marginBottom: 30 }}>
            <div style={{ fontFamily: codeFont, fontSize: 11, letterSpacing: '0.34em', color: cyan, textTransform: 'uppercase', marginBottom: 14, textShadow: `0 0 10px ${cyan}66` }}>
              ◉ Oracle channel
            </div>
            <h1 style={{ margin: 0 }}>
              <Typewriter
                text={data.title}
                speed={30}
                caretColor={lime}
                style={{
                  fontFamily: theme.fonts.heading,
                  fontWeight: 600,
                  fontSize: 'clamp(1.7rem, 4.4vw, 2.7rem)',
                  lineHeight: 1.08,
                  color: text,
                  textShadow: `0 0 30px ${lime}66`,
                  display: 'inline-block',
                }}
              />
            </h1>
            {data.subtitle && <p style={{ color: muted, fontSize: 15, margin: '12px 0 0', fontFamily: codeFont, letterSpacing: '0.04em' }}>{data.subtitle}</p>}
          </header>

          <div className="space-y-5">
            {blocks.map((block) => (
              <NorBlock
                key={block.id}
                block={block}
                answers={answers}
                answer={answers[block.id]}
                onAnswer={(a) => setAnswer(block.id, a)}
                play={play}
                tokens={{ lime, cyan, magenta, text, muted, codeFont }}
              />
            ))}
          </div>

          {complete && data.conclusion && (
            <div className="mt-10">
              <div className="nor-locked">
                <span>Oracle closed · signal locked</span>
                <div className="nor-line" />
              </div>
              <ExperienceClose
                conclusion={data.conclusion}
                theme={theme}
                values={values}
                outcome={data.scoring.mode === 'none' ? undefined : String(Math.round(rollup * 100))}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-block dispatch
// ─────────────────────────────────────────────────────────────────────────────

interface Tokens {
  lime: string;
  cyan: string;
  magenta: string;
  text: string;
  muted: string;
  codeFont?: string;
}

interface NorBlockProps {
  block: CompositionBlock;
  answers: Answers;
  answer: BlockAnswer | undefined;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  tokens: Tokens;
}

function NorBlock({ block, answers, answer, onAnswer, play, tokens }: NorBlockProps) {
  switch (block.type) {
    case 'stimulus':
      return <NorStimulus block={block} />;
    case 'rate-slider':
      return <NorRateSlider block={block} answers={answers} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'confidence':
      return <NorRateSlider block={block as unknown as RateSliderBlock} answers={answers} answer={answer} onAnswer={onAnswer} play={play} t={tokens} confidence />;
    case 'reveal':
      return <NorReveal block={block} answers={answers} play={play} />;
    case 'commit-box':
      return <NorCommitBox block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'choice':
      return <NorChoice block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'select-evidence':
      return <NorSelectEvidence block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'sort-zones':
      return <NorSortZones block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'flag-in-text':
      return <NorFlagInText block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'perspective-lens':
      return <NorPerspective block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    default:
      return null;
  }
}

function Panel({ children }: { children: React.ReactNode }) {
  return <div className="nor-panel">{children}</div>;
}

// ── stimulus ──────────────────────────────────────────────────────────────────
function NorStimulus({ block }: { block: StimulusBlock }) {
  return (
    <Panel>
      {block.content.heading && <div className="nor-kicker">{block.content.heading}</div>}
      <p className="nor-body">{block.content.body}</p>
    </Panel>
  );
}

// ── rate-slider (+ confidence) — the change-of-mind DELTA READOUT standout ──────
function NorRateSlider({
  block,
  answers,
  answer,
  onAnswer,
  play,
  t,
  confidence,
}: {
  block: RateSliderBlock;
  answers: Answers;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
  confidence?: boolean;
}) {
  const { min, max, step } = block.structure;
  const committed = (answer as { value: number } | undefined)?.value;
  const [pos, setPos] = useState<number>(committed ?? Math.round((min + max) / 2));
  const locked = committed !== undefined;
  const value = locked ? committed : pos;
  const prior = block.ref ? (answers[block.ref] as { value?: number } | undefined)?.value : undefined;
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const delta = prior !== undefined ? value - prior : undefined;

  return (
    <Panel>
      <div className="nor-kicker">{confidence ? 'Confidence' : prior !== undefined ? 'Revised reading' : 'Your reading'}</div>
      <p className="nor-prompt">{block.content.prompt}</p>
      <div className="nor-scale-val">{value}</div>
      <div className="nor-track">
        <div className="nor-rail" />
        {prior !== undefined && (
          <span
            className={`nor-trace${locked ? ' pulse' : ''}`}
            style={{ left: `${Math.min(pct(prior), pct(value))}%`, width: `${Math.abs(pct(value) - pct(prior))}%` }}
            aria-hidden
          />
        )}
        {prior !== undefined && <span className="nor-ghost" style={{ left: `${pct(prior)}%` }} aria-hidden />}
        <input
          type="range"
          className="nor-range nor-overlay"
          min={min}
          max={max}
          step={step ?? 1}
          value={value}
          disabled={locked}
          onChange={(e) => {
            setPos(Number(e.target.value));
            play('select');
          }}
        />
      </div>
      <div className="nor-ends">
        <span>{block.content.minLabel ?? min}</span>
        <span>{block.content.maxLabel ?? max}</span>
      </div>
      {prior !== undefined && delta !== undefined && (
        <div className="nor-delta">
          {delta === 0 ? (
            <>
              <span className="nor-first">FIRST {prior}</span> ▸ <span className="nor-rev">REVISED {value}</span> · <span className="nor-d">Δ 0 · held</span>
            </>
          ) : (
            <>
              <span className="nor-first">FIRST {prior}</span> ▸ <span className="nor-rev">REVISED {value}</span> · <span className="nor-d">Δ {delta > 0 ? '+' : ''}{delta}</span>
            </>
          )}
        </div>
      )}
      {!locked && (
        <button
          className="nor-btn"
          onClick={() => {
            play(prior !== undefined ? 'reveal' : 'commit');
            onAnswer({ value: pos });
          }}
        >
          {prior !== undefined ? 'Lock revised reading' : 'Lock reading'}
        </button>
      )}
    </Panel>
  );
}

// ── reveal — DECRYPTING scanline unveil once its ref commits ────────────────────
function NorReveal({ block, answers, play }: { block: RevealBlock; answers: Answers; play: Play }) {
  const unlocked = answers[block.ref] !== undefined;
  useEffect(() => {
    if (unlocked) play('reveal');
  }, [unlocked, play]);
  if (!unlocked) {
    return <div className="nor-sealed">▓▓ Encrypted — lock your reading to decrypt the signal ▓▓</div>;
  }
  return (
    <div className="nor-panel nor-reveal">
      <span className="nor-sweep" aria-hidden />
      <div className="nor-decrypt">
        <div className="nor-kicker">{block.content.heading || 'Decrypted signal'}</div>
        <p className="nor-body">{block.content.body}</p>
      </div>
    </div>
  );
}

// ── commit-box — held, then set into a terminal quote ─────────────────────────
function NorCommitBox({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: CommitBoxBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const committed = (answer as { text: string } | undefined)?.text;
  const [text, setText] = useState(committed ?? '');
  const locked = committed !== undefined;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const ok = words >= (block.structure.minWords ?? 1);
  return (
    <Panel>
      <div className="nor-kicker">Commit to the log</div>
      <p className="nor-prompt">{block.content.prompt}</p>
      {locked ? (
        <div className="nor-quote">{committed}</div>
      ) : (
        <>
          <textarea className="nor-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={block.content.placeholder} style={{ fontFamily: t.codeFont }} />
          <button
            className="nor-btn"
            disabled={!ok}
            onClick={() => {
              play('commit');
              onAnswer({ text: text.trim() });
            }}
          >
            Commit
          </button>
        </>
      )}
    </Panel>
  );
}

// ── choice ────────────────────────────────────────────────────────────────────
function NorChoice({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: ChoiceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const chosen = (answer as { optionId: string } | undefined)?.optionId;
  const chosenOpt = block.content.options.find((o) => o.id === chosen);
  return (
    <Panel>
      <p className="nor-prompt">{block.content.prompt}</p>
      <div className="space-y-2">
        {block.content.options.map((opt) => (
          <button
            key={opt.id}
            disabled={!!chosen}
            onClick={() => {
              play('select');
              onAnswer({ optionId: opt.id });
            }}
            className={`nor-opt${opt.id === chosen ? ' nor-on' : ''}`}
            style={{ opacity: chosen && opt.id !== chosen ? 0.5 : 1 }}
          >
            {opt.text}
          </button>
        ))}
      </div>
      {chosenOpt?.feedback && <p style={{ marginTop: 12, fontSize: '0.9rem', color: t.muted, fontStyle: 'italic' }}>{chosenOpt.feedback}</p>}
    </Panel>
  );
}

// ── select-evidence — glowing selections ──────────────────────────────────────
function NorSelectEvidence({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: SelectEvidenceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const committed = (answer as { selected: string[] } | undefined)?.selected;
  const [selected, setSelected] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const max = block.structure.maxSelections;
  const atMax = max !== undefined && selected.length >= max;
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : atMax ? s : [...s, id]));
  return (
    <Panel>
      {block.content.claim && <div className="nor-quote" style={{ marginBottom: 12 }}>{block.content.claim}</div>}
      <p className="nor-prompt">{block.content.prompt}</p>
      <div className="space-y-2">
        {block.content.items.map((it) => (
          <button
            key={it.id}
            disabled={locked || (!selected.includes(it.id) && atMax)}
            onClick={() => {
              play('select');
              toggle(it.id);
            }}
            className={`nor-opt${selected.includes(it.id) ? ' nor-on' : ''}`}
          >
            {it.text}
          </button>
        ))}
      </div>
      {max !== undefined && !locked && <p style={{ marginTop: 8, fontSize: '0.72rem', color: t.muted, letterSpacing: '0.06em' }}>{selected.length}/{max} tagged</p>}
      {!locked && (
        <button className="nor-btn" disabled={selected.length === 0} onClick={() => onAnswer({ selected })}>
          Lock selection
        </button>
      )}
    </Panel>
  );
}

// ── sort-zones ────────────────────────────────────────────────────────────────
function NorSortZones({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: SortZonesBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const committed = (answer as { placement: Record<string, string> } | undefined)?.placement;
  const [placement, setPlacement] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allPlaced = block.content.items.every((it) => placement[it.id]);
  return (
    <Panel>
      <p className="nor-prompt">{block.content.prompt}</p>
      <div className="space-y-3">
        {block.content.items.map((it) => (
          <div key={it.id}>
            <p style={{ fontSize: '0.92rem', color: t.text, margin: '0 0 6px' }}>{it.text}</p>
            <div className="flex flex-wrap gap-2">
              {block.content.zones.map((z) => (
                <button
                  key={z.id}
                  disabled={locked}
                  onClick={() => {
                    play('select');
                    setPlacement((p) => ({ ...p, [it.id]: z.id }));
                  }}
                  className={`nor-opt${placement[it.id] === z.id ? ' nor-on' : ''}`}
                  style={{ width: 'auto', padding: '6px 14px', fontSize: '0.82rem', borderRadius: 999 }}
                >
                  {z.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {!locked && (
        <button className="nor-btn" disabled={!allPlaced} onClick={() => onAnswer({ placement })}>
          Lock the sort
        </button>
      )}
    </Panel>
  );
}

// ── flag-in-text ──────────────────────────────────────────────────────────────
function NorFlagInText({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: FlagInTextBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const committed = (answer as { flagged: string[] } | undefined)?.flagged;
  const [flagged, setFlagged] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const toggle = (id: string) => setFlagged((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  return (
    <Panel>
      <p className="nor-prompt">{block.content.prompt}</p>
      <p className="nor-body" style={{ lineHeight: 1.9 }}>
        {block.content.segments.map((seg) => {
          const on = flagged.includes(seg.id);
          const wasRight = locked && seg.flagged === true;
          const wasWrong = locked && on && seg.flagged !== true;
          return (
            <span
              key={seg.id}
              onClick={() => {
                if (locked) return;
                play('select');
                toggle(seg.id);
              }}
              style={{
                cursor: locked ? 'default' : 'pointer',
                background: on ? (wasWrong ? `${t.magenta}22` : `${t.lime}1c`) : 'transparent',
                borderBottom: locked
                  ? `2px solid ${wasRight ? t.lime : wasWrong ? t.magenta : 'transparent'}`
                  : on
                    ? `2px solid ${t.lime}`
                    : '2px solid transparent',
                borderRadius: 3,
                padding: '0 2px',
              }}
            >
              {seg.text}{' '}
            </span>
          );
        })}
      </p>
      {!locked && (
        <button className="nor-btn" disabled={flagged.length === 0} onClick={() => onAnswer({ flagged })}>
          Flag {flagged.length} span{flagged.length === 1 ? '' : 's'}
        </button>
      )}
    </Panel>
  );
}

// ── perspective-lens ──────────────────────────────────────────────────────────
function NorPerspective({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: PerspectiveLensBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const committed = (answer as { takes: Record<string, string> } | undefined)?.takes;
  const [takes, setTakes] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allFilled = block.content.lenses.every((l) => (takes[l.id] ?? '').trim().length > 0);
  return (
    <Panel>
      <p className="nor-prompt">{block.content.prompt}</p>
      <div className="space-y-3">
        {block.content.lenses.map((lens) => (
          <div key={lens.id}>
            <p style={{ fontSize: '0.82rem', fontWeight: 600, color: t.cyan, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 3px', textShadow: `0 0 8px ${t.cyan}66` }}>{lens.label}</p>
            {lens.hint && <p style={{ fontSize: '0.78rem', color: t.muted, margin: '0 0 4px' }}>{lens.hint}</p>}
            {locked ? (
              <div className="nor-quote">{takes[lens.id]}</div>
            ) : (
              <textarea
                className="nor-input"
                rows={2}
                value={takes[lens.id] ?? ''}
                onChange={(e) => setTakes((tk) => ({ ...tk, [lens.id]: e.target.value }))}
                style={{ fontFamily: t.codeFont }}
              />
            )}
          </div>
        ))}
      </div>
      {!locked && (
        <button className="nor-btn" disabled={!allFilled} onClick={() => onAnswer({ takes })}>
          Lock these
        </button>
      )}
    </Panel>
  );
}
